import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardStep from './wizard-step';
import { useStepNavigation } from './use-step-navigation';
import { ServiceSupplierSignup } from '../../services/service-supplier-signup';
import PlaceSelector, { placeOptions } from '../page-supplier-presentation/PlaceSelector';

interface ProcurementStats {
  ongoingCount: number;
  marketplaceCount: number;
  recentCount: number;
}

/** Map stripped place values back to Swedish display labels */
const valueToLabel = new Map(placeOptions.map(o => [o.value, o.label]));

const formatPlaceList = (places: string[], and: string, more: string): string => {
  const labels = places.map(p => valueToLabel.get(p) || p);
  if (labels.length <= 2) return labels.join(` ${and} `);
  if (labels.length <= 4) return labels.slice(0, -1).join(', ') + ` ${and} ` + labels[labels.length - 1];
  return labels.slice(0, 3).join(', ') + ` ${and} ${labels.length - 3} ${more}`;
};

const StepPlaces: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const { state } = useSignupWizard();

  const [selectedPlaces, setSelectedPlaces] = useState<string[]>(state.places || []);
  const [stats, setStats] = useState<ProcurementStats | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef(0); // Guards against stale responses

  // Stable ref for areaIds to avoid re-triggering useEffect on context changes
  const areaIdsRef = useRef(state.areaIds);
  areaIdsRef.current = state.areaIds;

  const fetchStats = useCallback(async (places: string[]) => {
    if (places.length === 0) {
      setStats(null);
      return;
    }
    const requestId = ++requestRef.current;
    try {
      const data = await ServiceSupplierSignup.getProcurementStats(areaIdsRef.current, places);
      if (requestId === requestRef.current) setStats(data);
    } catch {
      if (requestId === requestRef.current) setStats(null);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchStats(selectedPlaces);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectedPlaces, fetchStats]);

  const { handleNext, handleBack } = useStepNavigation({
    currentStep: 3,
    nextPath: '/supplier-sign-up/about-you',
    prevPath: '/supplier-sign-up/categories',
    validate: () => selectedPlaces.length > 0,
    onBeforeNext: () => ({ places: selectedPlaces }),
    eventName: 'signup_wizard_step_places',
  });

  const canProceed = selectedPlaces.length > 0;
  const hasStats = selectedPlaces.length > 0 && stats !== null && stats.recentCount > 0;

  return (
    <WizardStep
      step={3}
      title={t('places.title', 'Var verkar ert företag?')}
      description={t('places.description', 'Verksamhetsområden')}
      onNext={handleNext}
      onBack={handleBack}
      canProceed={canProceed}
    >
      {/* Place selector with floating stats tooltip */}
      <div style={{ position: 'relative' }}>
        <PlaceSelector
          places={selectedPlaces}
          onChange={setSelectedPlaces}
          required
          noTooltip
          placeholderInsteadOfLabel
        />

        {/* Stats tooltip — floats to the left of the selector */}
        {hasStats && (
          <>
            <style>{`
              @keyframes tooltipSlideIn {
                from { opacity: 0; transform: translateX(8px); }
                to { opacity: 1; transform: translateX(0); }
              }
              @media (prefers-reduced-motion: reduce) {
                @keyframes tooltipSlideIn { from { opacity: 0; } to { opacity: 1; } }
              }
              .signup-wizard-stats-tooltip {
                margin-top: 12px;
              }
              .signup-wizard-stats-tooltip .signup-wizard-stats-arrow { display: none; }
              @media (min-width: 769px) {
                .signup-wizard-stats-tooltip {
                  position: absolute;
                  right: calc(100% + 16px);
                  top: 0;
                  margin-top: 0;
                  width: 260px;
                  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
                  animation: tooltipSlideIn 200ms ease-out;
                  z-index: 10;
                }
                .signup-wizard-stats-tooltip .signup-wizard-stats-arrow { display: block; }
              }
            `}</style>
          <div
            className="signup-wizard-stats-tooltip rounded-lg border px-4 py-3"
            style={{
              borderColor: 'var(--gray-200)',
              borderLeft: '3px solid var(--orange-500)',
              background: '#fff',
            }}
          >
            {/* Arrow pointing right — desktop only */}
            <div className="signup-wizard-stats-arrow" style={{
              position: 'absolute',
              right: '-6px',
              top: '16px',
              width: '12px',
              height: '12px',
              background: '#fff',
              border: '1px solid var(--gray-200)',
              borderLeft: 'none',
              borderBottom: 'none',
              transform: 'rotate(45deg)',
            }} />

            <div className="flex items-center gap-2">
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--orange-500)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="7 17 17 7" />
                <polyline points="7 7 17 7 17 17" />
              </svg>
              <span className="font-semibold text-sm" style={{ color: 'var(--gray-700)' }}>
                {t('places.statsTitle', 'Möjligheter i ert område')}
              </span>
            </div>

            <div className="text-xl font-bold" style={{ color: 'var(--orange-500)', fontVariantNumeric: 'tabular-nums', margin: '2px 0' }}>
              {stats!.recentCount} <span className="text-xs font-medium" style={{ color: 'var(--gray-600)' }}>{t('places.recentLabel', 'förfrågningar senaste 12 mån')}</span>
            </div>

            <div className="text-xs flex flex-col" style={{ color: 'var(--gray-500)' }}>
              <span>
                <strong style={{ color: 'var(--gray-700)' }}>{stats!.ongoingCount}</strong> {t('places.ongoingLabel', 'pågående förfrågningar')}
                {stats!.marketplaceCount > 0 && (
                  <>, {t('places.marketplaceOf', 'varav')} <strong style={{ color: 'var(--gray-700)' }}>{stats!.marketplaceCount}</strong> {t('places.marketplaceSuffix', 'på marknadsplatsen')}</>
                )}
              </span>
              <span>
                {t('places.inCategories', 'Inom {{count}} kategorier', { count: state.areaIds.length })} {t('places.inPlaces', 'i {{places}}', { places: formatPlaceList(selectedPlaces, t('places.and', 'och'), t('places.more', 'till')) })}
              </span>
            </div>
          </div>
          </>
        )}
      </div>

    </WizardStep>
  );
};

export default StepPlaces;
